import { useParams } from "react-router-dom";
import { useActiveShopGridByShop } from "queries/shop_grid";
import { useFamilyIndexByBrand } from "queries/family";
import { useTranslation } from "react-i18next";
import { Button, Grid, Paper, Typography, FormControlLabel} from "@mui/material";
import useShopGridController from "modules/shop_grid/useShopGridController";
import { Api } from "queries/types";
import CellInventoryForm from "./CellInventoryForm";
import ShopGridView from "modules/shop_grid/ShopGridView";
import { useState } from "react";
import LegendModal from "./LegendModal";
import { useShopShow } from "queries/shops";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import Switch from '@mui/material/Switch';
import CellInventoryModal from "./CellInventoryModal";
import BreadCrumbShop from "../index/BreadCrumbShop";

interface ShopInventoryProps {
  shopGrid: Api.ShopGrid;
  families: Api.Family[];
}

const ShopInventory = ({ shopGrid, families }: ShopInventoryProps) => {
  const { grid, selectedCells, unselectAll, toggleCell, configureCells, gridFurnitures, gridFamilies, closeViewCell, switchToViewMode, selectedViewCell, viewMode } =
    useShopGridController(shopGrid, { families });
  const [showLegend, setShowLegend] = useState(false);
  const { t } = useTranslation();

  const cellFormOpen = selectedCells.length > 0;
  const cellViewOpen = !!selectedViewCell;
  const gridShadow = cellFormOpen ? "inset 0px -4px 4px 0px #00000040" : "none";

  return (
    <Grid
      container
      direction="column"
      // Use as much space as possible by cancelling container margin-top of 32px
      sx={{ position: "absolute", left: 0, width: "100%", marginTop: { sm: 0 } }}
    >
      <Grid item xs={12} sx={{ maxWidth: "100% !important" }}>
        <Paper sx={{ width: "100%", boxSizing: "border-box", p: { xs: 1, sm: 2 }, pt: { xs: 0, sm: 2 } }}>
          <Grid container flexWrap="nowrap" direction="column">
            <Grid item container direction="row" alignItems="center">
              <Button variant="text" color="info" onClick={() => setShowLegend(true)}>
                {t("shop.inventory.legend.display")}
              </Button>
              <Typography fontWeight={300} fontSize="14px" alignItems="center" display="flex" marginRight={'10px'} marginLeft={'auto'}>
                {t("shop.inventory.viewMode")} :
              </Typography>
              <ModeEditOutlinedIcon sx={{ marginRight: '10px' }} />
              <FormControlLabel
                control={
                  <Switch onChange={() => switchToViewMode(!viewMode) } aria-label={t("a11y.viewModeZoom")} />
                }
                label={<ZoomInIcon  aria-hidden="true" sx={{ mx: "auto" }} />}
                sx={{ alignItems: 'flex-end' }}
              />
            </Grid>
            {showLegend && (
              <LegendModal furnitures={gridFurnitures} families={gridFamilies} onClose={() => setShowLegend(false)} />
            )}
            <Grid item sx={{ overflow: "auto", boxShadow: gridShadow }} xs={12}>
              <ShopGridView
                grid={grid}
                selectedCells={selectedCells}
                toggleCell={toggleCell}
                mode="inventory"
                maxHeight="70vh"
                viewMode={!!viewMode}
              />
            </Grid>
            {cellFormOpen && (
              <Grid item xs={12} key={selectedCells.map((cell) => `${cell.yAxis}-${cell.xAxis}`).join()}>
                <CellInventoryForm
                  cells={selectedCells}
                  families={families}
                  onSubmit={(sections) => {
                    configureCells({ sections });
                    unselectAll();
                  }}
                  unselectAll={unselectAll}
                />
              </Grid>
            )}
            {cellViewOpen && selectedViewCell && (
              <CellInventoryModal sections={selectedViewCell.sections} onClose={() => closeViewCell()} />
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

const ShopInventoryPage = () => {
  const { t } = useTranslation();
  const { erpId } = useParams<{ erpId: string }>();
  const { data: shop, isLoading: isLoadingShop } = useShopShow(erpId!);
  const { data: activeGrid, isLoading: isLoadingGrid } = useActiveShopGridByShop(shop);
  const { data: families = [], isFetching: isLoadingFamilies } = useFamilyIndexByBrand(shop?.brandId);

  if (isLoadingShop || isLoadingGrid || isLoadingFamilies) return null;

  return <>
    <Typography component="h1" variant="h6" sx={{ mb: 1 }}>
      {t("shop.index.inventory")}
    </Typography>
    <BreadCrumbShop shop={shop} label="shop.index.inventory" />
    <ShopInventory shopGrid={activeGrid!} families={families} />
  </>;
};

export default ShopInventoryPage;
